import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import { Plus, Trash2, Users } from 'lucide-react';
import { ParticipantData } from '@/types/signup';
import { Trip, SkiPass } from '@/types';
import { getSkiPassPriceAtTripStart, calculateAgeAtDate } from '@/utils/calculations';
import { useI18n } from '@/hooks/useI18n';

interface CompanionSectionProps {
  companions: ParticipantData[];
  trip: Trip;
  onCompanionsChange: (companions: ParticipantData[]) => void;
}

const CompanionSection: React.FC<CompanionSectionProps> = ({
  companions,
  trip,
  onCompanionsChange
}) => {
  const { t, formatCurrency } = useI18n();
  const maxCompanions = 7; // 8 total - 1 primary = 7 companions

  const addCompanion = () => {
    if (companions.length < maxCompanions) {
      const newCompanion: ParticipantData = {
        name: '',
        dateOfBirth: '',
        isSkiClubMember: false,
        preferredSkiPass: undefined,
      };
      onCompanionsChange([...companions, newCompanion]);
    }
  };

  const removeCompanion = (index: number) => {
    const updatedCompanions = companions.filter((_, i) => i !== index);
    onCompanionsChange(updatedCompanions);
  };

  const updateCompanion = (index: number, field: keyof ParticipantData, value: string | boolean | undefined) => {
    const updatedCompanions = companions.map((companion, i) => 
      i === index ? { ...companion, [field]: value } : companion
    );
    onCompanionsChange(updatedCompanions);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">👥 {t('signup.companions')}</Typography>
        {companions.length > 0 && (
          <Chip 
            label={t('signup.companion', { count: companions.length })}
            size="small"
            sx={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('signup.companionDescription')}
      </Typography>

      {companions.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            🚶‍♀️ {t('signup.travelingSolo')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Plus style={{ width: 16, height: 16 }} />}
            onClick={addCompanion}
            sx={{ minWidth: 160 }}
          >
            {t('signup.addCompanion')}
          </Button>
        </Card>
      ) : (
        <Box>
          {companions.map((companion, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    🧑‍🤝‍🧑 {t('signup.companionNumber', { number: index + 1 })}
                  </Typography>
                  <IconButton
                    onClick={() => removeCompanion(index)}
                    size="small"
                    sx={{ color: 'error.main' }}
                  >
                    <Trash2 style={{ width: 16, height: 16 }} />
                  </IconButton>
                </Box>

                <Grid container spacing={3}>
                  {/* Name */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={`👤 ${t('participant.fullName')}`}
                      value={companion.name}
                      onChange={(e) => updateCompanion(index, 'name', e.target.value)}
                      required
                      placeholder={t('signup.enterCompanionName')}
                    />
                  </Grid>

                  {/* Date of Birth */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label={`🎂 ${t('participant.dateOfBirth')}`}
                      type="date"
                      value={companion.dateOfBirth}
                      onChange={(e) => updateCompanion(index, 'dateOfBirth', e.target.value)}
                      required
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>

                  {/* Ski Club Membership */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={companion.isSkiClubMember}
                          onChange={(e) => updateCompanion(index, 'isSkiClubMember', e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          ⛷️ {t('signup.skiClubMember')}
                          {companion.isSkiClubMember && (
                            <Chip
                              label={t('signup.benefitsApply')}
                              size="small"
                              sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </Grid>

                  {/* Ski Pass Selection */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>🎿 {t('participant.preferredSkiPass')}</InputLabel>
                      <Select
                        value={companion.preferredSkiPass || 'none'}
                        onChange={(e) => updateCompanion(index, 'preferredSkiPass', e.target.value === 'none' ? undefined : e.target.value)}
                        label={`🎿 ${t('participant.preferredSkiPass')}`}
                      >
                        <MenuItem value="none">{t('skipass.noSkiPass')}</MenuItem>
                        {trip.skiPasses && trip.skiPasses.length > 0 ? (
                          trip.skiPasses.map((skiPass) => {
                            const ageAtTripStart = calculateAgeAtDate(companion.dateOfBirth, trip.startDate);
                            const price = getSkiPassPriceAtTripStart(companion.dateOfBirth, trip.startDate, trip.skiPassAgeLevels, skiPass);
                            return (
                              <MenuItem key={skiPass.id} value={skiPass.id}>
                                {skiPass.name} - {formatCurrency(price)} {ageAtTripStart > 0 && `(${t('participant.age')}: ${ageAtTripStart})`}
                              </MenuItem>
                            );
                          })
                        ) : (
                          <MenuItem value="no-passes-available" disabled>
                            {t('skipass.noSkiPassesConfigured')}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {companions.length < maxCompanions && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Plus style={{ width: 16, height: 16 }} />}
                onClick={addCompanion}
                sx={{ minWidth: 160 }}
              >
                {t('signup.addAnotherCompanion')}
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                {t('signup.maximumCompanions', { count: maxCompanions })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
};

export default CompanionSection;
